import { useQuery } from '@tanstack/react-query';
// Import concrete modules (not the @/lib and @/utils barrels): these hooks are
// instantiated at module-load time via createEntityCrudHooks(), and the barrels
// form a cycle back into @/hooks that leaves the factory undefined at that point.
import { supabase } from '@/lib/supabase';
import { handleSupabaseError } from '@/lib/supabase-error-handler';
import { getObjectDiff } from '@/utils/activity-logger';
import { useLoggedMutation } from './useLoggedMutation';

/**
 * CRUD hook factory for the simple "class taxonomy" tables — currently
 * `class_locations` and `class_categories`. Both are a title-only row with
 * created-by admin attribution, activity logging on every mutation, and a
 * cross-invalidation of the class list on delete. The two per-entity hook
 * files were byte-identical apart from the noun (and the by-id query-key
 * shape), so this factory holds the shared logic and each entity file is a
 * thin config + named re-exports.
 */
export interface EntityCrudHooksConfig {
  /** Postgres table name, e.g. 'class_locations'. */
  table: string;
  /** Activity-log entity type, e.g. 'class_location'. Actions are `${entityType}_created|updated|deleted`. */
  entityType: string;
  /** Singular human label for error/operation messages, e.g. 'class location'. */
  label: string;
  /** Plural human label, e.g. 'class locations'. */
  labelPlural: string;
  /** PascalCase noun for hookName telemetry, e.g. 'ClassLocation' → 'useCreateClassLocation'. */
  pascalName: string;
  /** List query-key root from query-keys.ts (e.g. CLASS_LOCATION_QUERY_KEYS.ALL). */
  allKey: readonly unknown[];
  /** Builds the by-id query key (the group's BY_ID factory). */
  byId: (id: string) => readonly unknown[];
  /** Extra list keys to invalidate after a delete (e.g. the class list). */
  relatedKeysOnDelete?: readonly (readonly unknown[])[];
}

export function createEntityCrudHooks<TEntity extends { id: string; title: string }, TCreateData, TUpdateData>(
  config: EntityCrudHooksConfig
) {
  const { table, entityType, label, labelPlural, pascalName, allKey, byId, relatedKeysOnDelete = [] } = config;

  function useList() {
    return useQuery({
      queryKey: allKey,
      queryFn: async () => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .order('title', { ascending: true });

        if (error) handleSupabaseError(error, { operation: `fetching ${labelPlural}` });
        return data as TEntity[];
      },
    });
  }

  function useOne(id: string) {
    return useQuery({
      queryKey: byId(id),
      queryFn: async () => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .eq('id', id)
          .single();

        if (error) handleSupabaseError(error, { operation: `fetching ${label}` });
        return data as TEntity;
      },
      enabled: !!id,
    });
  }

  function useCreate() {
    return useLoggedMutation({
      hookName: `useCreate${pascalName}`,
      operation: `creating ${label}`,
      mutationFn: async (data: TCreateData, { admin, log }) => {
        const { display_name, email } = await admin();
        const record = {
          ...(data as Record<string, unknown>),
          created_by_name: display_name,
          created_by_email: email,
        };

        const { data: created, error } = await supabase
          .from(table)
          .insert([record])
          .select()
          .single();

        if (error) handleSupabaseError(error, { operation: `creating ${label}` });

        log({ action: `${entityType}_created`, entityType, entityId: created.id, metadata: record });
        return created as TEntity;
      },
      invalidate: (queryClient) => queryClient.invalidateQueries({ queryKey: allKey }),
    });
  }

  function useUpdate(id: string) {
    return useLoggedMutation({
      hookName: `useUpdate${pascalName}`,
      operation: `updating ${label}`,
      mutationFn: async (data: TUpdateData, { log }) => {
        // Get the original record for change tracking
        const { data: original, error: fetchError } = await supabase
          .from(table)
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) handleSupabaseError(fetchError, { operation: `fetching ${label} for update` });

        // `table` is a runtime string, so the row type is `any` and supabase-js's
        // excess-property guard (RejectExcessProperties) cannot be evaluated for a
        // generic TUpdateData. Widen to a plain record; the callers' types still
        // constrain what reaches this hook.
        const { data: updated, error } = await supabase
          .from(table)
          .update(data as Record<string, unknown>)
          .eq('id', id)
          .select()
          .single();

        if (error) handleSupabaseError(error, { operation: `updating ${label}` });

        const changes = getObjectDiff(original, updated);
        if (Object.keys(changes).length > 0) {
          log({ action: `${entityType}_updated`, entityType, entityId: id, changes });
        }
        return updated as TEntity;
      },
      invalidate: (queryClient) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: allKey }),
          queryClient.invalidateQueries({ queryKey: byId(id) }),
        ]),
    });
  }

  function useDelete() {
    return useLoggedMutation({
      hookName: `useDelete${pascalName}`,
      operation: `deleting ${label}`,
      mutationFn: async (id: string, { log }) => {
        // Get the record details before deletion for activity logging
        const { data: record, error: fetchError } = await supabase
          .from(table)
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) handleSupabaseError(fetchError, { operation: `fetching ${label} for deletion` });

        const { error } = await supabase
          .from(table)
          .delete()
          .eq('id', id);

        if (error) handleSupabaseError(error, { operation: `deleting ${label}` });

        log({
          action: `${entityType}_deleted`,
          entityType,
          entityId: id,
          changes: { title: { from: record.title, to: null } },
          metadata: { title: record.title },
        });
      },
      invalidate: (queryClient) =>
        Promise.all(
          [allKey, ...relatedKeysOnDelete].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        ),
    });
  }

  return { useList, useOne, useCreate, useUpdate, useDelete };
}
